import { CompensationHistoryInterface } from '../../models/history';
import { DEFAULT_SEARCH_INFORMATION_FIELDS, DEFAULT_SEARCH_RESULTS } from '../../constants/default';
import { Field } from '../../models/home';
import { formatDateInDDMMYYYY, formatDateInYYYYMMDD, getUtcDate } from '../../helpers/date';
import { useEffect, useState } from 'react';
import { useHistoryProvider } from '../../providers/HistoryProvider';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

export const useCompensationHistoryNonIFS = () => {
    const historyContext = useHistoryProvider();
    const { i18n, t } = useTranslation();

    const [searchFields, setSearchFields] = useState<Field[]>(DEFAULT_SEARCH_INFORMATION_FIELDS);
    const [searchResults, setSearchResults] = useState<CompensationHistoryInterface[]>([]);
    const [showSearchResults, setShowSearchResults] = useState(false);

    useEffect(() => {
        const onLanguageChange = () => handleOnClickClear();
        i18n.on('languageChanged', onLanguageChange);
        return () => i18n.off('languageChanged', onLanguageChange);
    }, [i18n]);

    useEffect(() => {
        if (!historyContext.historyNonIfs.length) {
            setSearchResults(DEFAULT_SEARCH_RESULTS);
        } else {
            const searchResultsMap = historyContext.historyNonIfs.map(v => {
                const isError = v.voucherStatusCode === 'FAILED' || v.voucherStatusCode === 'ERR';
                return {
                    bookingReference: v.pnrLocator,
                    comments: v?.voucherIssuanceComment ?? '',
                    customer: `${v.passengerFirstName} ${v.passengerLastName}`,
                    date: formatDateInDDMMYYYY(getUtcDate(v.flightScheduledAt)),
                    email: v.passengerEmailAddress,
                    error: isError,
                    errorMessage: isError ? (v.responseStatusCode != null ? t(`notifications.history.${v.responseStatusCode.toString()}`) || '' : '') : '',
                    flight: `${v.marketingCarrier}${v.flightLegNumber}`,
                    groupId: v.groupId,
                    id: v.voucherIds[0] ? v.voucherIds[0] : uuidv4(),
                    qty: v.qty ?? 1,
                    reason: v?.voucherIssuanceReason ?? '',
                    reasonId: v?.reasonId,
                    categoryId: v?.categoryId,
                    cabinId: v?.cabinId,
                    status: v?.voucherStatusCode ?? '',
                    subType: v?.voucherSubTypeCode ?? '',
                    type: v?.voucherTypeCode ?? '',
                    voucherIds: v.voucherIds,
                    requestId: v.requestId
                }
            });
            setSearchResults(searchResultsMap);
        }
    }, [historyContext.historyNonIfs, t]);

    const assignRequiredFieldErrors = () => {
        setSearchFields(searchFields.map(f => {
            return {
                ...f,
                requiredError: t(`validations.${f.name}`)
            }
        }));
    };

    const handleOnClickClear = () => {    
        setShowSearchResults(false);
        setSearchFields(DEFAULT_SEARCH_INFORMATION_FIELDS);
    };

    const handleOnClickSearch = (pageNumber?: number, pageSize?: string) => {
        if (searchFields.findIndex(f => f.error || f.requiredError) > -1) {
            return;
        }
        const nonEmptySearchFieldIndex = searchFields.findIndex(f => f.value);
        if (nonEmptySearchFieldIndex > -1) {
            setShowSearchResults(true);
            const lastName = searchFields[0].value;
            const date = searchFields[1].value ? formatDateInYYYYMMDD(searchFields[1].value): searchFields[1].value;
            const bookingReference = searchFields[2].value;
            const ticketNumber = searchFields[3].value;
            const page = pageNumber ?? 0;
            const limit = pageSize ? Number(pageSize) : historyContext.historyNonIfsPageState.size;
            historyContext.getNonIFSHistory(lastName, limit, page, bookingReference, date, ticketNumber);
        } else {
            assignRequiredFieldErrors();
        }
    };

    return {
        historyContext,
        searchFields,
        searchResults,
        showSearchResults,
        handleOnClickClear,
        handleOnClickSearch,
        setSearchFields,
        t
    };
};

